import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsPlayFill, BsPauseFill, BsStopFill } from "react-icons/bs";

/**
 * 현재 재생 중인 음성메모 audio 엘리먼트 (같은 창 안에서 하나만 재생되도록 관리)
 * 20261015 음성메모 재생 UI 개선
 * @since 2026-10-15.
 */
let activeAudio = null;

/**
 * 초를 mm:ss 형식으로 변환하는 메서드
 * @param {Number} sec - 초
 * @returns {String} mm:ss (알 수 없는 경우 --:--)
 * @since 2026-10-15.
 */
const formatTime = (sec) => {
    if (sec === undefined || sec === null || !isFinite(sec) || sec < 0) return "--:--";

    const total = Math.floor(sec);
    return String(Math.floor(total / 60)).padStart(2, "0") + ":" + String(total % 60).padStart(2, "0");
};

/**
 * 음성메모 재생 컴포넌트
 * [재생/일시정지] [정지] 재생된시간 / 전체시간 형태로 표시한다.
 * - 재생 중 다른 음성메모를 재생하면 이전 메모는 그 위치에서 일시정지된다.
 * - 재생이 끝나거나 정지하면 00:00으로 돌아간다.
 * 20261015 기존 PLAY 버튼(항상 처음부터 재생)을 대체
 *
 * @param {String} src - 음성메모 URL (DataURL)
 * @param {String} align - "left"(받은 메모) | "right"(보낸 메모)
 * @param {Boolean} sendFailed - 전송 실패 여부 (보낸 메모)
 * @author hjkim.
 * @since 2026-10-15.
 */
const AudioMemoPlayer = ({ src, align = "left", sendFailed = false }) => {
    const { t } = useTranslation();

    const audioRef = useRef(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [curTime, setCurTime] = useState(0);
    const [duration, setDuration] = useState(NaN);

    // 언마운트 시 재생 중이면 정지
    useEffect(() => {
        const audio = audioRef.current;

        return () => {
            if (audio) {
                audio.pause();
                if (activeAudio === audio) activeAudio = null;
            }
        };
    }, []);

    // 음성메모가 바뀌면 상태 초기화
    useEffect(() => {
        setIsPlaying(false);
        setCurTime(0);
        setDuration(NaN);
    }, [src]);

    const handleLoadedMetadata = () => {
        const audio = audioRef.current;
        if (audio && isFinite(audio.duration)) {
            setDuration(audio.duration);
        }
    };

    const handleDurationChange = () => {
        const audio = audioRef.current;
        if (audio && isFinite(audio.duration)) {
            setDuration(audio.duration);
        }
    };

    const handleTimeUpdate = () => {
        const audio = audioRef.current;
        if (audio) {
            setCurTime(audio.currentTime);
        }
    };

    const handlePlay = () => {
        setIsPlaying(true);
    };

    const handlePause = () => {
        setIsPlaying(false);
    };

    const handleEnded = () => {
        const audio = audioRef.current;
        if (audio) {
            audio.currentTime = 0;
            if (activeAudio === audio) activeAudio = null;
        }
        setCurTime(0);
        setIsPlaying(false);
    };

    // 재생 / 일시정지
    const handlePlayPause = () => {
        const audio = audioRef.current;
        if (!audio || !src) return;

        if (!audio.paused) {
            audio.pause();
            return;
        }

        // 다른 음성메모가 재생 중이면 그 위치에서 일시정지
        if (activeAudio && activeAudio !== audio) {
            activeAudio.pause();
        }
        activeAudio = audio;

        const playPromise = audio.play();
        if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch((err) => {
                console.log("AudioMemoPlayer - play() err => ", err);
                setIsPlaying(false);
            });
        }
    };

    // 정지 (처음으로)
    const handleStop = () => {
        const audio = audioRef.current;
        if (!audio) return;

        audio.pause();
        audio.currentTime = 0;
        if (activeAudio === audio) activeAudio = null;
        setCurTime(0);
        setIsPlaying(false);
    };

    // 키보드(Enter / Space) 지원
    const handleKeyDown = (e, func) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            func();
        }
    };

    const isIdle = !isPlaying && curTime === 0;

    return (
        <div className={"audio-memo-player-wrap " + (align === "right" ? "send" : "rcv")}>
            <div className={"audio-memo-player" + (isPlaying ? " playing" : "")}>
                <span
                    role="button"
                    tabIndex={0}
                    className="amp-btn"
                    title={isPlaying ? t("음성메모_일시정지", { ns: "classTalk" }) : t("음성메모_재생", { ns: "classTalk" })}
                    aria-label={isPlaying ? t("음성메모_일시정지", { ns: "classTalk" }) : t("음성메모_재생", { ns: "classTalk" })}
                    onClick={handlePlayPause}
                    onKeyDown={(e) => handleKeyDown(e, handlePlayPause)}
                >
                    {isPlaying ? <BsPauseFill /> : <BsPlayFill />}
                </span>
                <span
                    role="button"
                    tabIndex={0}
                    className={"amp-btn" + (isIdle ? " idle" : "")}
                    title={t("음성메모_정지", { ns: "classTalk" })}
                    aria-label={t("음성메모_정지", { ns: "classTalk" })}
                    onClick={handleStop}
                    onKeyDown={(e) => handleKeyDown(e, handleStop)}
                >
                    <BsStopFill />
                </span>
                <span className="amp-time">
                    {formatTime(curTime)} / {formatTime(duration)}
                </span>
            </div>
            {
                sendFailed &&
                <span className="audio-memo-send-failed">{t("음성메모_전송_실패_표시", { ns: "classTalk" })}</span>
            }
            <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                style={{ display: "none" }}
                onLoadedMetadata={handleLoadedMetadata}
                onDurationChange={handleDurationChange}
                onTimeUpdate={handleTimeUpdate}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handleEnded}
            />
        </div>
    );
};

export default AudioMemoPlayer;
